import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import {
  Button,
  Container,
  Divider,
  Icon,
  Image,
  Label,
  List,
  Placeholder,
} from 'semantic-ui-react';
import PubSub from 'pubsub-js';

import { socialFieldList, socialIconList } from '../constants';
import CompanyPhotoGallery from './CompanyPhotoGallery';
import { useBreakpoints } from '$app/common/utils/mediaQueries';
import RegistrationTopic from '$app/modules/registrationPage/topics/RegistrationPage.topic';

const CompanyDetail = ({ title, description, tagList, listItems }) => (
  <div
    css={{
      display: 'flex',
      flexDirection: 'column',
      marginBottom: 40,
      fontSize: 13,
      wordSpacing: 2,
    }}
  >
    <p
      css={{
        fontSize: 20,
        fontWeight: 700,
        color: '#333333',
        marginBottom: 10,
      }}
    >
      {title}
    </p>
    {tagList ? (
      <div
        css={{
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        {tagList.map(tag => (
          <Label
            key={tag}
            basic
            css={{
              '&.ui.basic.label': {
                border: '1px solid #000000',
                display: 'flex',
                alignItems: 'center',
                marginBottom: 5,
              },
            }}
          >
            {tag}
          </Label>
        ))}
      </div>
    ) : listItems ? (
      <div>
        <List as="ul">
          {listItems?.map(text => (
            <List.Item key={text} as="li">
              {text}
            </List.Item>
          ))}
        </List>
      </div>
    ) : (
      <p css={{ fontWeight: 'normal', color: '#333333' }}>{description}</p>
    )}
  </div>
);

CompanyDetail.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  tagList: PropTypes.arrayOf(PropTypes.string).isRequired,
  listItems: PropTypes.arrayOf(PropTypes.string).isRequired,
};

const CompanyDetailsPage = ({ isFetching, data }) => {
  const { isSmDown } = useBreakpoints();

  const renderContent = useCallback(
    fieldName => {
      const paragraph = data?.[fieldName];

      if (!paragraph) return null;
      return documentToReactComponents(paragraph);
    },
    [data],
  );

  const CompanyDetailsPageLoader = (
    <Container css={{ marginTop: 25, marginBottom: 100 }}>
      <Placeholder
        css={{
          '&.ui.placeholder': {
            width: '100%',
            maxWidth: '100%',
            height: 500,
            marginBottom: 20,
          },
        }}
      >
        <Placeholder.Image />
      </Placeholder>
      <div
        css={{
          display: 'flex',
        }}
      >
        <div
          css={{
            width: '120px',
            height: '120px',
            boxShadow: '5px 5px 50px rgba(0, 0, 0, 0.15)',
            borderRadius: '10px',
            background: '#fff',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 15,
          }}
        >
          <Placeholder css={{ height: 100, width: 100 }}>
            <Placeholder.Image />
          </Placeholder>
        </div>
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            flex: 1,
            marginLeft: '30px',
          }}
        >
          <Placeholder
            fluid
            css={{
              '&&&.ui.fluid.placeholder': {
                width: 150,
                marginBottom: 15,
              },
            }}
          >
            <Placeholder.Line />
          </Placeholder>
          <div
            css={{
              display: 'flex',
            }}
          >
            <Placeholder css={{ height: 25, width: 80 }}>
              <Placeholder.Image />
            </Placeholder>
          </div>
        </div>
      </div>
    </Container>
  );

  return isFetching ? (
    CompanyDetailsPageLoader
  ) : (
    <>
      <CompanyPhotoGallery
        companyPhotoList={data?.images}
        companyVideoList={data?.videos}
      />
      <Container css={{ marginTop: 25 }}>
        <div
          css={{
            display: 'flex',
            flexDirection: isSmDown ? 'column' : 'row',
            justifyContent: 'space-between',
          }}
        >
          <div
            css={{
              display: 'flex',
            }}
          >
            <div
              css={{
                width: '120px',
                height: '120px',
                boxShadow: '5px 5px 50px rgba(0, 0, 0, 0.15)',
                borderRadius: '10px',
                background: '#fff',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 15,
              }}
            >
              <Image src={data?.logo} />
            </div>
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                flex: 1,
                marginLeft: '30px',
              }}
              display="flex"
            >
              <p
                gutterBottom
                css={{
                  fontSize: 32,
                  marginBottom: 13,
                  color: '#333333',
                  fontWeight: 700,
                }}
              >
                {data?.name}
              </p>
              <div
                css={{
                  display: 'flex',
                  flexWrap: 'wrap',
                }}
              >
                {data?.tags?.map(tag => (
                  <Label
                    key={tag}
                    basic
                    css={{
                      '&.ui.basic.label': {
                        border: '1px solid #000000',
                        textTransform: 'uppercase',
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: 10,
                        marginBottom: 5,
                      },
                    }}
                  >
                    {tag}
                  </Label>
                ))}
              </div>
            </div>
          </div>
          <div
            css={{
              display: 'flex',
              flexDirection: isSmDown ? 'row' : 'column',
              justifyContent: 'space-between',
              alignItems: isSmDown ? 'center' : 'flex-start',
              marginTop: isSmDown ? 15 : 0,
            }}
          >
            <div
              css={{
                display: 'flex',
                alignItems: 'center',
                alignSelf: 'flex-end',
              }}
            >
              {socialIconList.map(
                (socialIcon, index) =>
                  data?.[socialFieldList[index]] && (
                    <button
                      key={socialIcon}
                      type="button"
                      onClick={() =>
                        window
                          .open(data?.[socialFieldList[index]], '_blank')
                          .focus()
                      }
                      css={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: 'black',
                        width: 24,
                        height: 24,
                        marginLeft: '6px',
                        borderRadius: '100%',
                        cursor: 'pointer',
                        border: 'none',
                      }}
                    >
                      <Icon
                        name={socialIcon}
                        css={{
                          '&.icon': {
                            color: 'white',
                            margin: 0,
                          },
                        }}
                      />
                    </button>
                  ),
              )}
            </div>
            <Button
              content="View open jobs"
              onClick={() =>
                PubSub.publish(RegistrationTopic.OPEN, {
                  openJobsLink: data?.openJobsLink,
                })
              }
              css={{
                '&.ui.button': {
                  background: '#6C2D8A',
                  color: '#fff',
                  borderRadius: 5,
                  width: isSmDown ? 'max-content' : '100%',

                  '&:hover': {
                    filter: 'brightness(85%)',
                    background: '#6C2D8A',
                    color: '#fff',
                  },
                },
              }}
            />
          </div>
        </div>
        <Divider
          css={{
            '&.ui.divider': {
              marginTop: 40,
              marginBottom: 44,
            },
          }}
        />
      </Container>
      <div
        css={{
          position: 'relative',
          marginBottom: '55px',
        }}
      >
        <Container>
          <div
            css={{
              display: 'flex',
              flexDirection: isSmDown ? 'column' : 'row',
            }}
          >
            <div css={{ display: 'flex', flexDirection: 'column', flex: 0.8 }}>
              <CompanyDetail
                title="About"
                description={renderContent('aboutSection')}
              />
              <CompanyDetail
                title="What it's like to work here"
                description={renderContent('workCulture')}
              />
              <CompanyDetail
                title="We hire people who are"
                tagList={data?.candidateQualities}
              />
              <CompanyDetail
                title="You'll fall in love with"
                listItems={data?.employerQualities}
              />
            </div>
            <div
              css={{
                position: 'relative',
                flex: 0.2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                zIndex: 10,
                marginBottom: 20,
              }}
            >
              <Image
                size="small"
                circular
                alt="test"
                src={data?.memberOneImageLink}
                css={{
                  '&&&.ui.image': {
                    border: '1px solid #000000',
                    width: 150,
                    height: 150,
                    objectFit: 'cover',
                  },
                }}
              />
              <p
                css={{
                  fontWeight: 700,
                  color: '#333333',
                  fontSize: 18,
                  margin: 0,
                  marginTop: 18,
                }}
              >
                {data?.memberOneName}
              </p>
              <p css={{ color: '#333333', fontSize: 13 }}>
                {data?.memberOnePositionTitle}
              </p>
              {isSmDown && (
                <Image
                  src="https://images.snaphunt.com/assets/images/chat.png"
                  css={{
                    '&.ui.image': {
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      height: 200,
                      zIndex: -1,
                    },
                  }}
                />
              )}
            </div>
          </div>
          <div
            css={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: isSmDown ? 100 : 240,
              color: '#fff',
              background:
                'radial-gradient(132.3% 291.36% at 1.17% 18.65%, #D91C5C 0%, #8C2886 65%, #6C2D8A 100%)',
              borderRadius: 15,
            }}
          >
            <p
              css={{
                fontSize: isSmDown ? 16 : 46,
                color: '#fff',
                textAlign: 'center',
                fontWeight: 700,
                marginBottom: 10,
              }}
            >
              {data?.name} is hiring!
            </p>
            <Button
              content="View open jobs"
              onClick={() =>
                PubSub.publish(RegistrationTopic.OPEN, {
                  openJobsLink: data?.openJobsLink,
                })
              }
              css={{
                '&.ui.button': {
                  background: '#fff',
                  color: '#000',
                  width: 'max-content',
                  borderRadius: 5,

                  '&:hover': {
                    filter: 'brightness(85%)',
                    background: '#fff',
                    color: '#000',
                  },
                },
              }}
            />
            <Image
              src="https://images.snaphunt.com/assets/images/Group-16.png"
              css={{
                '&.ui.image': {
                  objectFit: 'cover',
                  position: 'absolute',
                  right: isSmDown ? 10 : 30,
                  top: isSmDown ? 10 : 30,
                  width: isSmDown ? 60 : 128,
                  height: isSmDown ? 60 : 128,
                },
              }}
            />
          </div>
          {!isSmDown && (
            <Image
              src="https://images.snaphunt.com/assets/images/chat.png"
              css={{
                '&.ui.image': {
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  height: 300,
                  zIndex: -1,
                },
              }}
            />
          )}
        </Container>
      </div>
    </>
  );
};

CompanyDetailsPage.propTypes = {
  isFetching: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.object).isRequired,
};

export default CompanyDetailsPage;
